/**
 * GuestUpgradePrompt - Modal to encourage guest users to link their account
 * 
 * Triggered when guest users try to:
 * - Save a 2nd Tarot reading
 * - Save chat history
 * - Access certain features
 * 
 * Design: Gentle nudge, not a hard block. User can dismiss and continue.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { useFirebaseAuth } from '@/hooks/useFirebaseAuth';

// Google brand icon
const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const GuestUpgradePrompt = ({ 
    isOpen, 
    onClose, 
    onSuccess,
    trigger = 'reading' // 'reading' | 'chat' | 'feature'
}) => {
    const { linkGuestToGoogle, isLoading, error, setError } = useFirebaseAuth();
    const [linkSuccess, setLinkSuccess] = useState(false);

    const messages = {
        reading: {
            title: 'Keep This Memory Forever',
            subtitle: 'Link your account to save all your readings',
            icon: '🔮'
        },
        chat: {
            title: 'Your Cosmic Conversations',
            subtitle: 'Link your account to never lose your chat history',
            icon: '💬'
        },
        feature: {
            title: 'Unlock Full Access',
            subtitle: 'Link your account to access all features',
            icon: '✨'
        }
    };

    const { title, subtitle, icon } = messages[trigger] || messages.reading;

    const handleLinkGoogle = async () => {
        try {
            const userData = await linkGuestToGoogle();
            if (userData) {
                setLinkSuccess(true);
                setTimeout(() => {
                    onSuccess?.(userData);
                    onClose?.();
                }, 1500);
            }
        } catch (err) {
            console.error('Account linking failed:', err);
        }
    };

    const handleSkip = () => {
        setError(null);
        onClose?.();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {/* Backdrop */}
                <motion.div 
                    className="absolute inset-0 bg-black/80 backdrop-blur-md"
                    onClick={handleSkip}
                />

                {/* Modal */}
                <motion.div
                    className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl"
                    style={{
                        background: 'linear-gradient(180deg, rgba(15, 15, 17, 0.98) 0%, rgba(8, 8, 10, 0.98) 100%)',
                        border: '1px solid rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 0 60px rgba(212, 175, 55, 0.1)'
                    }}
                    initial={{ scale: 0.9, y: 30, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, y: 30, opacity: 0 }}
                >
                    {/* Close Button */}
                    <button
                        onClick={handleSkip}
                        className="absolute top-4 right-4 p-2 rounded-full transition-colors z-20"
                        style={{ background: 'rgba(212, 175, 55, 0.1)', color: 'rgba(212, 175, 55, 0.6)' }}
                        data-testid="close-upgrade-prompt"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="p-8 text-center">
                        {linkSuccess ? (
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                            >
                                <div className="text-5xl mb-4">🎉</div>
                                <h3 className="font-cinzel text-xl text-primary mb-2">You're All Set!</h3>
                                <p className="text-primary/60 text-sm">
                                    Your cosmic journey is now saved forever
                                </p>
                            </motion.div>
                        ) : (
                            <>
                                {/* Icon */}
                                <div className="text-5xl mb-4">{icon}</div>

                                {/* Title */}
                                <h3 
                                    className="font-cinzel text-xl mb-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    {title}
                                </h3>

                                {/* Subtitle */}
                                <p className="text-primary/60 text-sm mb-6">{subtitle}</p>

                                {/* Link Google Button */}
                                <motion.button
                                    onClick={handleLinkGoogle}
                                    disabled={isLoading}
                                    className="w-full py-4 px-6 rounded-xl flex items-center justify-center gap-3 font-inter font-medium mb-3"
                                    style={{
                                        background: 'white',
                                        color: '#1f1f1f',
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                                    }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    data-testid="link-google-btn"
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <GoogleIcon />
                                            Link Google Account
                                        </>
                                    )}
                                </motion.button>

                                {/* Skip Button */}
                                <button
                                    onClick={handleSkip}
                                    className="text-primary/50 text-sm hover:text-primary/70 transition-colors"
                                    data-testid="skip-upgrade-btn"
                                >
                                    Maybe later
                                </button>

                                {/* Error */}
                                {error && (
                                    <motion.p 
                                        className="mt-4 text-red-400 text-sm"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                    >
                                        {error}
                                    </motion.p>
                                )}
                            </>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default GuestUpgradePrompt;
